/**
 * Visibilidad del plano (solo VISTA): qué se ve y qué no cuando se combinan los ojos de capa, las tramas y
 * «Aislar / Ocultar selección». Nada de esto toca cantidades, mediciones, selección ni lo guardado: lo
 * oculto sigue contando en la hoja.
 *
 * Un solo conjunto de ocultos: los ojos (capas/tramas) mandan sobre «aislar»: lo que la persona apagó
 * con un ojo no reaparece porque aísle algo (se vuelve a ver con su ojo o con «Mostrar todo»).
 */

export type ModoVisibilidad = 'todo' | 'aislar' | 'ocultar';

export interface EntidadVisible {
  handle: string;
  capa: string;
  /** Es una trama (HATCH): por el TIPO de la entidad, sin importar su capa. */
  esTrama: boolean;
}

/** ¿Algún ojo (capa o tramas) está apagado? */
export function hayOjosApagados(capasOcultas: ReadonlySet<string>, tramasOcultas: boolean): boolean {
  return capasOcultas.size > 0 || tramasOcultas;
}

/**
 * Handles que deben verse. Una entidad oculta por ojo (su capa apagada, o es trama con tramas apagadas) nunca
 * se ve; con la selección: `aislar` deja solo lo seleccionado, `ocultar` quita lo seleccionado. Sin selección,
 * el modo no hace nada (todo lo que no apagó un ojo se ve).
 */
export function handlesVisibles(
  entidades: Iterable<EntidadVisible>,
  opciones: {
    capasOcultas: ReadonlySet<string>;
    tramasOcultas: boolean;
    seleccion: ReadonlySet<string>;
    modo: ModoVisibilidad;
  },
): { visibles: Set<string>; ocultos: Set<string> } {
  const { capasOcultas, tramasOcultas, seleccion } = opciones;
  const modo: ModoVisibilidad = seleccion.size === 0 ? 'todo' : opciones.modo;
  const visibles = new Set<string>();
  const ocultos = new Set<string>();
  for (const e of entidades) {
    const porOjo = capasOcultas.has(e.capa) || (tramasOcultas && e.esTrama);
    const porSel = modo === 'aislar' ? !seleccion.has(e.handle) : modo === 'ocultar' ? seleccion.has(e.handle) : false;
    if (porOjo || porSel) ocultos.add(e.handle);
    else visibles.add(e.handle);
  }
  return { visibles, ocultos };
}

/** ¿Todos esos ids están en el conjunto de ocultos? (vacío = no). */
export function todosOcultos<T>(ocultos: ReadonlySet<T>, ids: readonly T[]): boolean {
  return ids.length > 0 && ids.every((id) => ocultos.has(id));
}

/** Prende o apaga un grupo: si todo el grupo está oculto lo muestra; si no, lo oculta entero. No muta. */
export function alternarGrupoOculto<T>(ocultos: ReadonlySet<T>, ids: readonly T[]): Set<T> {
  const n = new Set(ocultos);
  if (todosOcultos(ocultos, ids)) for (const id of ids) n.delete(id);
  else for (const id of ids) n.add(id);
  return n;
}

/** Prende o apaga una capa por nombre. No muta. */
export function alternarCapaOculta(ocultas: ReadonlySet<string>, capa: string): Set<string> {
  return alternarGrupoOculto(ocultas, [capa]);
}
